import { getLogger } from "@expert/logging";

import { getRootDispatcher } from "@/analytics";

import type { AgentSdkBase } from "./agentSdkBase";
import type { Task } from "./task";
import type { AgentActivity } from "./types";

import { useCallbackStore } from "../sessions/callback.store";
import { getSelectedSession, getSessionByTaskId } from "../sessions/getters";
import { getCallbackState } from "../sessions/hooks";
import { useSessionStore } from "../sessions/session.store";
import { sdkEventBus } from "./eventBus";
import { useAgentStore } from "./store";
import { isVoiceTask } from "./voice";

const logger = getLogger({ module: "callbacks" });

export async function scheduleCallbackNow<TTask extends Task>(
    agentSdk: AgentSdkBase<TTask>,
    callbackDelay: number,
    callbackMdn: string,
    callbackPendingAgentActivity: AgentActivity,
    wrappingAgentActivity: AgentActivity | undefined,
) {
    function handleSetCallbackNowState(voiceTask: Task) {
        const scheduledFor = Date.now() + callbackDelay * 1000;

        setCallback({
            callbackType: "CallbackNow",
            scheduledAt,
            scheduledFor,
            callbackDelay,
            callbackMDN: callbackMdn,
            originalTask: voiceTask,
        });
    }

    async function handleWithinActiveTask(task: Task) {
        const cachedTaskId = task.id;

        const callbackState = {
            callbackType: "CallbackNow",
            scheduledAt,
            // We don't schedule it yet since we are waiting for the task to wrap
            // THis time should reflect the end of call time  + delay
            scheduledFor: 0,
            callbackDelay,
            callbackMDN: callbackMdn,
            originalTask: task,
        } as const;

        setCallback(callbackState);
        subTaskCompletedCallbackNow(cachedTaskId);
        if (agentSdk.serviceProvider === "connect") {
            await agentSdk.setAgentActivity(callbackPendingAgentActivity);
            useAgentStore.getState().setPendingActivity(undefined);
        }
    }

    async function handleWithinWrappingTask(task: Task) {
        // Order is important here, since task completion has side effects in the agentSdk
        // 1. We set our callback state
        // 2. We complete the task
        // 3. onTaskCompleted() will set our activity to "Callback Pending" if a callback state is set
        logger.trace({ taskId: task.id }, "Scheduling callback now within wrapping task");
        handleSetCallbackNowState(task);
        if (agentSdk.serviceProvider === "connect") {
            await agentSdk.setAgentActivity(callbackPendingAgentActivity);
            useAgentStore.getState().setPendingActivity(undefined);
        }
        const session = getSessionByTaskId(task.id);
        if (!session) {
            logger.warn({ taskId: task.id }, "Cannot end wrapup - no session found for task");
            return;
        }
        await agentSdk.endWrapup("CallbackScheduled", task.partner, session.id, task);

        // No need to set agent activity, SDK should do this as a side effect of task completing.
        // TODO: Future: This is a fragility point
    }

    /**
     * We are scheduling a callback when we no longer have a task
     * This may happen when the agent cancels a callback and schedules it again
     */
    async function handleWithoutTask() {
        const session = getSelectedSession();

        const previousTask = Array.from(session.tasks.values()).at(-1);

        if (!previousTask)
            throw new Error(
                "Cannot schedule a callback now without a previous task, no previous task found on session",
            );

        if (session.wrappingState) {
            void getRootDispatcher().dispatchBusinessEvent("WrappingEnded", {
                wrappingEndReason: "CallbackScheduled",
            });
            useSessionStore.getState().clearWrappingState(session.id);
        }

        await agentSdk.setAgentActivity(callbackPendingAgentActivity);
        handleSetCallbackNowState(previousTask);
    }

    const agentActivity = useAgentStore.getState().activity;
    const setCallback = useCallbackStore.getState().setCallback;
    const session = getSelectedSession();

    const currentTask = session.currentTask;

    const scheduledAt = Date.now();

    try {
        if (currentTask) {
            if (currentTask.status === "wrapping" || agentActivity === wrappingAgentActivity || session.wrappingState) {
                await handleWithinWrappingTask(currentTask);
            } else {
                void handleWithinActiveTask(currentTask);
            }
        } else {
            await handleWithoutTask();
        }

        return true;
    } catch (error) {
        logger.warn({ error }, "Failed to schedule callback now");
        return false;
    }
}

let existingCallbackNowSubscription: (() => void) | null = null;

export function subTaskCompletedCallbackNow(taskId: string) {
    logger.trace({ taskId }, "Subscribing to task wrapping event for callback now");

    // If a callback if scheduled again, make sure we are not piling up subscriptions
    if (existingCallbackNowSubscription) existingCallbackNowSubscription();

    existingCallbackNowSubscription = sdkEventBus.once(
        "task_wrapping",
        (eventTask) => {
            existingCallbackNowSubscription = null;

            //TODO: Remove when we have chat support, this will no longer be needed and more serves as a touch-point error
            if (!isVoiceTask(eventTask)) throw new Error("Unsupported task type in callback handler");

            logger.trace({ taskId, eventTask }, "Task wrapping event received for callback now");

            // TODO: Ensure that cancelling a callback will clear our callback state
            //    - I changed the behavior here to rely on the session callback state, and not the UI store "dirty" state

            const existingCallbackState = getCallbackState();
            logger.trace({ taskId, existingCallbackState }, "Checking callback state for callback now");

            // If the callback was cancelled or changed we don't want to set a bad callbackNow state
            if (existingCallbackState?.callbackType === "CallbackNow") {
                const setCallback = useCallbackStore.getState().setCallback;

                const scheduledFor = Date.now() + (existingCallbackState.callbackDelay ?? 0) * 1000;

                logger.trace(
                    {
                        taskId,
                        scheduledFor,
                        newState: {
                            ...existingCallbackState,
                            scheduledFor,
                        },
                    },
                    "Setting callback now state",
                );

                setCallback({
                    ...existingCallbackState,
                    scheduledFor,
                });
            }
        },
        (voiceTask) => voiceTask.id === taskId,
    );
}
